import React, { useState } from 'react';
import { Star, Play, FileText, Presentation, Bookmark, History, Check } from 'lucide-react';
import TopicIcon from './icons/TopicIcon';
import BookmarkButton from './BookmarkButton';
import { thumbnailFor } from '../../utils/educationContent';
import { primaryTopic } from '../../data/educationTaxonomy';
import { recordOpen, useEducationLibrary } from '../../utils/educationLibrary';

/*
 * The one way an Education Center item is drawn.
 *
 *   hero   full-width 16:9 image, title and one meta line below          (newest featured item)
 *   rail   fixed-width 16:9 card for horizontal shelves                    (featured, saved, new)
 *   row    16:9 thumb at left, title and meta at right, bookmark at the end (topic pages, search)
 *
 * The image is the card. Items without art get placeholder art in the same shape: the primary
 * topic's gradient and glyph. Badges sit on the image, metadata stays to one line, and the
 * description is left to the viewer.
 */

const TYPE_LABEL = { document: 'PDF', presentation: 'Slides', video: 'Video' };

const fmtDuration = (s) => {
  if (!s) return null;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
};

const inlinePdf = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}response-content-disposition=inline` : url);

/** 16:9 image or placeholder art, with badges */
export const Thumb = ({ item, shelf, hideFeatured = false, index, rounded = 'rounded-xl', iconSize = 'w-8 h-8', sizes }) => {
  // An image that fails to load (missing slide render, revoked URL) falls back to placeholder art
  const [failed, setFailed] = useState(null);
  const { fractionOf, completedOf } = useEducationLibrary();
  const fraction = item.fileType === 'video' ? fractionOf(item.id) : 0;
  const done = completedOf(item.id);
  const inProgress = item.fileType === 'video' && !done && fraction > 0.02;
  const candidate = thumbnailFor(item, sizes || 640);
  const src = candidate && failed !== candidate ? candidate : null;
  const topic = primaryTopic(item);
  const color = (topic && topic.color) || 'from-gray-500 to-gray-600';
  const dur = item.fileType === 'video' ? fmtDuration(item.duration) : null;
  const label = dur || TYPE_LABEL[item.fileType] || null;
  return (
    <div className={`relative aspect-video w-full overflow-hidden ${rounded} bg-gray-100 dark:bg-gray-800`}>
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={() => setFailed(src)}
          // A full-page PDF render should show its title block, not the middle of the page
          className={`absolute inset-0 w-full h-full object-cover ${item.fileType === 'document' ? 'object-top' : ''}`}
        />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${color} flex items-center justify-center text-white/90`}>
          <TopicIcon type={topic ? topic.icon : null} className={iconSize} />
        </div>
      )}
      {item.fileType === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-9 h-9 rounded-full bg-black/45 backdrop-blur-[2px] flex items-center justify-center">
            <Play size={16} className="text-white fill-white ml-0.5" />
          </div>
        </div>
      )}
      {item.featured && !shelf && !hideFeatured && (
        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-400 text-[10px] font-bold text-amber-950 shadow">
          <Star size={10} className="fill-current" /> Featured
        </span>
      )}
      {index != null && (
        <span className="absolute top-1.5 left-1.5 min-w-[1.4rem] h-[1.4rem] px-1 rounded-md bg-black/65 text-[11px] font-bold text-white flex items-center justify-center tabular-nums">{index}</span>
      )}
      {shelf && (
        <span className={`absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-white ${shelf === 'continue' ? 'bg-emerald-600' : 'bg-black/55'}`}>
          {shelf === 'saved' && <Bookmark size={10} className="fill-current" />}
          {shelf === 'recent' && <History size={10} />}
          {shelf === 'continue' && <Play size={10} className="fill-current" />}
          {shelf === 'saved' ? 'Saved' : shelf === 'continue' ? 'Continue' : 'Recent'}
        </span>
      )}
      {done && index != null && (
        <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow" aria-label="Completed">
          <Check size={12} strokeWidth={3} />
        </span>
      )}
      {inProgress && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/35" aria-hidden="true">
          <div className="h-full bg-emerald-500" style={{ width: `${Math.round(fraction * 100)}%` }} />
        </div>
      )}
      {label && (
        <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/65 text-[10px] font-semibold text-white tabular-nums">
          {item.fileType === 'document' && <FileText size={10} />}
          {item.fileType === 'presentation' && <Presentation size={10} />}
          {label}
        </span>
      )}
    </div>
  );
};

const Meta = ({ item, className = '' }) => {
  const topic = primaryTopic(item);
  const bits = [topic && topic.label, item.subcategory].filter(Boolean);
  if (!bits.length) return null;
  return <div className={`text-xs text-gray-500 dark:text-gray-400 truncate ${className}`}>{bits.join(' · ')}</div>;
};

/** Anchor for PDFs (browser viewer, new tab), button for everything else */
const Open = ({ item, onOpen, className, children }) => {
  if (item.fileType === 'document') {
    return (
      <a href={inlinePdf(item.downloadURL)} target="_blank" rel="noopener noreferrer" onClick={() => recordOpen(item)} className={`block ${className}`}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(item)} className={`block text-left ${className}`}>
      {children}
    </button>
  );
};

const ContentCard = ({ item, variant = 'row', onOpen, shelf, hideFeatured = false, index, width = 'w-44', bookmark = true, className = '' }) => {
  if (variant === 'hero') {
    return (
      <div className={`relative ${className}`}>
        <Open item={item} onOpen={onOpen} className="w-full group">
          <div className="shadow-card group-hover:shadow-card-hover transition-shadow rounded-2xl">
            <Thumb item={item} shelf={shelf} hideFeatured={hideFeatured} rounded="rounded-2xl" iconSize="w-14 h-14" sizes={1280} />
          </div>
          <div className="pt-3 pr-10">
            <h3 className="font-display text-[19px] leading-snug font-bold text-gray-900 dark:text-white line-clamp-2">{item.title}</h3>
            <Meta item={item} className="mt-1" />
          </div>
        </Open>
        {bookmark && <BookmarkButton item={item} className="absolute right-0 bottom-0 mb-1" />}
      </div>
    );
  }

  if (variant === 'rail') {
    return (
      <div className={`relative snap-start flex-shrink-0 ${width} ${className}`}>
        <Open item={item} onOpen={onOpen} className="w-full group">
          <div className="shadow-card group-hover:shadow-card-hover transition-shadow rounded-xl">
            <Thumb item={item} shelf={shelf} hideFeatured={hideFeatured} sizes={480} />
          </div>
          <div className="pt-2 pr-8">
            <h3 className="text-[13.5px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-2">{item.title}</h3>
            <Meta item={item} className="mt-0.5 text-[11px]" />
          </div>
        </Open>
        {bookmark && <BookmarkButton item={item} size={16} className="absolute right-0 bottom-0 -mr-1.5 -mb-1" />}
      </div>
    );
  }

  // row
  return (
    <div className={`relative ${className}`}>
      <Open item={item} onOpen={onOpen} className="w-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-3 pr-11 shadow-card hover:border-emerald-500 dark:hover:border-emerald-400 hover:shadow-card-hover transition-all">
        <div className="flex items-center gap-3">
          <div className="w-28 flex-shrink-0">
            <Thumb item={item} shelf={shelf} index={index} rounded="rounded-lg" iconSize="w-6 h-6" sizes={320} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold leading-snug text-gray-900 dark:text-white line-clamp-2">{item.title}</h3>
            <Meta item={item} className="mt-1" />
          </div>
        </div>
      </Open>
      {bookmark && <BookmarkButton item={item} className="absolute top-1/2 -translate-y-1/2 right-1.5" />}
    </div>
  );
};

/** A horizontal shelf of rail cards */
export const ContentRail = ({ items, onOpen, shelfOf, width, hideFeatured = false }) => (
  <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide snap-x snap-mandatory scroll-px-6">
    {items.map((item) => (
      <ContentCard key={item.id} item={item} variant="rail" onOpen={onOpen} shelf={shelfOf ? shelfOf(item) : undefined} width={width} hideFeatured={hideFeatured} />
    ))}
  </div>
);

/** A vertical list of row cards */
export const ContentRows = ({ items, onOpen, shelfOf }) => (
  <div className="space-y-3">
    {items.map((item) => (
      <ContentCard key={item.id} item={item} variant="row" onOpen={onOpen} shelf={shelfOf ? shelfOf(item) : undefined} />
    ))}
  </div>
);

export default ContentCard;
